import type {
  AddSwitchPairRequest,
  ExistingSnapshotResponse,
  FlowInstancePortfoliosResponse,
  GoalPortfolioResponse,
  ModelPortfolioCandidatesResponse,
  SnapshotInvalidation,
  SwitchPairClearResponse,
  SwitchPairDeleteResponse,
  SwitchPairResponse,
  UpdateExistingHoldingRequest,
  UpdateExistingHoldingResponse,
  UpdateGoalPortfolioRequest,
  UpdateSwitchPairRequest,
} from "@repo/shared/types";
import { del, get, patch, post } from "../_client";

const BASE = "/internal/flow-instances";

// --- Portfolios ---

export function getPortfolios(goalId: string) {
  return get<GoalPortfolioResponse[]>(`${BASE}/goals/${goalId}/portfolios`);
}

/** QL-676 — every portfolio of the instance, grouped by goal, in one request. */
export function getInstancePortfolios(instanceId: string) {
  return get<FlowInstancePortfoliosResponse>(`${BASE}/${instanceId}/portfolios`);
}

export function getPortfolio(portfolioId: string) {
  return get<GoalPortfolioResponse>(`${BASE}/portfolios/${portfolioId}`);
}

export function createPortfolio(goalId: string, data: Record<string, unknown>) {
  return post<any>(`${BASE}/goals/${goalId}/portfolios`, data);
}

export function updatePortfolio(portfolioId: string, data: UpdateGoalPortfolioRequest) {
  return patch<any & { refreshHints?: SnapshotInvalidation }>(
    `${BASE}/portfolios/${portfolioId}`,
    data satisfies UpdateGoalPortfolioRequest,
  );
}

/** QL-72 — model portfolios the advisor may pick from (all risk scores). */
export function getModelPortfolioCandidates(portfolioId: string) {
  return get<ModelPortfolioCandidatesResponse>(
    `${BASE}/portfolios/${portfolioId}/model-candidates`,
  );
}

export function deletePortfolio(portfolioId: string) {
  return del<{ success: boolean }>(`${BASE}/portfolios/${portfolioId}`);
}

// --- Existing-portfolio snapshot (followup comparison) ---

export function getExistingSnapshot(portfolioId: string) {
  return get<ExistingSnapshotResponse>(`${BASE}/portfolios/${portfolioId}/existing-snapshot`);
}

/** Advisor's keep/sell choice for an unmatchable holding (QL-203). */
export function updateExistingHolding(
  portfolioId: string,
  positionId: string,
  data: UpdateExistingHoldingRequest,
) {
  return patch<UpdateExistingHoldingResponse>(
    `${BASE}/portfolios/${portfolioId}/existing-snapshot/${positionId}`,
    data satisfies UpdateExistingHoldingRequest,
  );
}

// --- Switch pairs (QL-283) ---

export function addSwitchPair(portfolioId: string, data: AddSwitchPairRequest) {
  return post<SwitchPairResponse>(
    `${BASE}/portfolios/${portfolioId}/switch-pairs`,
    data satisfies AddSwitchPairRequest,
  );
}

export function updateSwitchPair(
  portfolioId: string,
  pairId: string,
  data: UpdateSwitchPairRequest,
) {
  return patch<SwitchPairResponse>(
    `${BASE}/portfolios/${portfolioId}/switch-pairs/${pairId}`,
    data satisfies UpdateSwitchPairRequest,
  );
}

export function deleteSwitchPair(portfolioId: string, pairId: string) {
  return del<SwitchPairDeleteResponse>(`${BASE}/portfolios/${portfolioId}/switch-pairs/${pairId}`);
}

/** "Clear switch" — every pair of one source holding, one server-side
 * snapshot regeneration. */
export function clearSwitchPairs(portfolioId: string, sourcePositionId: string) {
  return del<SwitchPairClearResponse>(
    `${BASE}/portfolios/${portfolioId}/switch-pairs?sourcePositionId=${encodeURIComponent(sourcePositionId)}`,
  );
}

// --- Portfolio Products ---

export function addPortfolioProduct(
  portfolioId: string,
  data: {
    productId: string;
    amount?: number;
    units?: number;
    weight: number;
  },
) {
  return post<any & { refreshHints?: SnapshotInvalidation }>(
    `${BASE}/portfolios/${portfolioId}/products`,
    data,
  );
}

export function updatePortfolioProduct(
  productId: string,
  data: {
    amount?: number;
    units?: number;
    weight?: number;
    order?: number;
  },
) {
  return patch<any & { refreshHints?: SnapshotInvalidation }>(
    `${BASE}/portfolios/products/${productId}`,
    data,
  );
}

export function deletePortfolioProduct(productId: string) {
  return del<{ success: boolean; portfolioId: string; refreshHints?: SnapshotInvalidation }>(
    `${BASE}/portfolios/products/${productId}`,
  );
}
